import { FC, ReactNode } from 'react';
import { createUseStyles } from 'react-jss';
import { CaseloadReading, READING_LABEL, READING_ORDER } from '@innerhive-internal/innerhive-common';
import { BRAND, CHART_INK, READING_COLOR } from './agencyChartTheme';

const useStyles = createUseStyles({
    row: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        gap: 8,
        marginBottom: 14
    },

    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '5px 11px 5px 9px',
        borderRadius: 20,
        border: `1px solid ${BRAND.tealEdge}`,
        background: 'var(--ion-background-color)',
        font: 'inherit',
        fontSize: 12.5,
        lineHeight: 1.3,
        color: CHART_INK.strongLabel,
        textAlign: 'left' as const
    },

    // The affordances of a control, kept off a key that only reads.
    pressable: {
        cursor: 'pointer',

        '&:hover, &:focus-visible': {
            borderColor: BRAND.deepTeal,
            background: BRAND.tealWash
        }
    },

    chipOn: {
        borderColor: BRAND.coral,
        background: BRAND.coralWash,

        '&:hover, &:focus-visible': {
            borderColor: BRAND.coral
        }
    },

    swatch: {
        width: 11,
        height: 11,
        borderRadius: 3,
        flexShrink: 0
    },

    count: {
        fontWeight: 600,
        color: BRAND.deepTeal,
        fontVariantNumeric: 'tabular-nums'
    }
});

/**
 * The map's colour key, each entry counting the clients drawn in that reading, and on screen each
 * a way to narrow the map to them: pressed again, or the clients entry, widens it back. A
 * caseload too young to compare offers only the incident reading, which needs no comparison. The
 * grey cells have no entry: not enough to compare is the absence of a reading, not one to filter to.
 *
 * On paper, with nothing to press, it is a key and nothing more.
 */
export const CaseloadReadingFilters: FC<{
    counts: { [key in CaseloadReading]: number };
    clientCount: number;
    /** Whether the caseload has the history the comparisons need; without it only the incident reading is offered. */
    hasHistory: boolean;
    /** The reading the map is narrowed to, when it is. */
    filter?: CaseloadReading;
    /** Whether the map is narrowed at all: the clients entry reads as pressed only when it is not. */
    narrowed: boolean;
    onToggleReading?: (reading: CaseloadReading) => void;
    onClear?: () => void;
    /** Sits at the far end of the row. */
    aside?: ReactNode;
}> = ({ counts, clientCount, hasHistory, filter, narrowed, onToggleReading, onClear, aside }) => {
    const classes = useStyles();
    const readings = READING_ORDER.filter(reading => reading !== 'unknown' && (hasHistory || reading === 'incident'));
    const pressable = Boolean(onToggleReading);

    const chip = (params: { key: string; reading?: CaseloadReading; count: number; label: string; isOn: boolean; onPress?: () => void }) => {
        const { key, reading, count, label, isOn, onPress } = params;
        const content = (
            <>
                {reading && <span className={classes.swatch} style={{ background: READING_COLOR[reading] }} />}
                <span className={classes.count} data-id="caseload-filter-count">{count}</span>
                {label}
            </>
        );
        const dataId = reading ? 'caseload-filter' : 'caseload-filter-all';

        if (!pressable) {
            return <span key={key} className={classes.chip} data-id={dataId} data-reading={reading}>{content}</span>;
        }
        return (
            <button
                key={key}
                type="button"
                className={`${classes.chip} ${classes.pressable} ${isOn ? classes.chipOn : ''}`}
                data-id={dataId}
                data-reading={reading}
                aria-pressed={isOn}
                onClick={onPress}
            >
                {content}
            </button>
        );
    };

    return (
        <div className={classes.row} data-id="caseload-filters">
            {chip({ key: 'all', count: clientCount, label: clientCount === 1 ? 'Client' : 'Clients', isOn: !narrowed, onPress: onClear })}
            {readings.map(reading => chip({
                key: reading,
                reading,
                count: counts[reading],
                label: READING_LABEL[reading],
                isOn: filter === reading,
                onPress: () => onToggleReading?.(reading)
            }))}
            {aside}
        </div>
    );
};
